import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { batchCreateOtherFinancialItems } from '@/modules/Report/components/OtherFinancials/services'
import { BatchItemRequest } from '@/modules/Report/components/OtherFinancials/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem, IconButton, Typogaphy } from 'vnetwork-ui'
import * as yup from 'yup'
import AddIcons from '@/icons/AddIcons'
import DeleteIcons from '@/icons/DeleteOutlineIcons'

export interface CreateOtherFinancialModalProps extends ModalProps {
    refetch?: () => void
}

interface FormData {
    report_year: number
    report_month: number
    currency: string
    revenues: BatchItemRequest[]
    expenses: BatchItemRequest[]
}

const currentYear = dayjs().year()
const CreateOtherFinancialModal: React.FC<CreateOtherFinancialModalProps> = ({
    open,
    onClose,
    refetch,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Form schema
    const schema = useMemo(
        () =>
            yup.object().shape({
                report_year: yup.number().required(t('required')),
                report_month: yup.number().required(t('required')),
                currency: yup.string().required(t('required')),
                revenues: yup.array().of(
                    yup.object().shape({
                        item_name: yup.string().required(t('required')),
                        amount: yup.number().min(0).required(t('required')),
                        notes: yup.string(),
                    })
                ),
                expenses: yup.array().of(
                    yup.object().shape({
                        item_name: yup.string().required(t('required')),
                        amount: yup.number().min(0).required(t('required')),
                        notes: yup.string(),
                    })
                ),
            }),
        [t]
    )

    const defaultValues: FormData = useMemo(
        () => ({
            report_year: currentYear,
            report_month: dayjs().month() + 1,
            currency: 'VND',
            revenues: [{ item_name: '', amount: 0, notes: '' }],
            expenses: [{ item_name: '', amount: 0, notes: '' }],
        }),
        []
    )

    const formControl = useForm<FormData>({
        defaultValues,
        // @ts-ignore
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset } = formControl

    const {
        fields: revenueFields,
        append: appendRevenue,
        remove: removeRevenue,
    } = useFieldArray({
        control,
        name: 'revenues',
    })

    const {
        fields: expenseFields,
        append: appendExpense,
        remove: removeExpense,
    } = useFieldArray({
        control,
        name: 'expenses',
    })

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (open) {
            reset(defaultValues)
        }
    }, [open, reset, defaultValues])

    const onSubmit = async (values: FormData) => {
        try {
            setIsSubmitting(true)

            // Filter out empty items
            const revenues = values.revenues.filter(
                (r) => r.item_name && r.amount > 0
            )
            const expenses = values.expenses.filter(
                (e) => e.item_name && e.amount > 0
            )

            if (revenues.length === 0 && expenses.length === 0) {
                toast.error(t('at_least_one_item_required'))
                setIsSubmitting(false)
                return
            }

            // Call batch create API
            await batchCreateOtherFinancialItems({
                report_month: `${values.report_year}-${values.report_month}`,
                revenues: revenues.map((r) => ({
                    ...r,
                    currency: values.currency,
                })),
                expenses: expenses.map((e) => ({
                    ...e,
                    currency: values.currency,
                })),
            })

            // Invalidate queries
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_OTHER_FINANCIALS],
            })

            // Show success message
            toast.success(t('create-success'))

            // Refetch and close
            refetch?.()
            onClose()
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error('Failed to create financial items:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('create_other_financial_items')}
            maxWidth="lg"
        >
            <form
                onSubmit={handleSubmit(onSubmit as any)}
                className="space-y-4 p-4"
            >
                {/* Basic Info Section */}
                <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                    <FormItem
                        control={control}
                        name="report_year"
                        type="select"
                        label={t('report_year')}
                        options={Array.from({ length: 12 }, (_, index) => ({
                            label: `${t('year')} ${currentYear - index}`,
                            value: currentYear - index,
                        }))}
                        allowClear={false}
                    />
                    <FormItem
                        control={control}
                        name="report_month"
                        type="select"
                        label={t('report_month')}
                        options={Array.from({ length: 12 }, (_, index) => ({
                            label: `${t('month')} ${index + 1}`,
                            value: index + 1,
                        }))}
                        allowClear={false}
                    />
                </div>

                {/* Revenues Section */}
                <div className="space-y-2 bg-gray-50 p-4 rounded-md">
                    <div className="flex justify-between items-center">
                        <Typogaphy className="font-medium text-primary">
                            {t('revenues')}
                        </Typogaphy>
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<AddIcons />}
                            onClick={() =>
                                appendRevenue({
                                    item_name: '',
                                    amount: 0,
                                    notes: '',
                                })
                            }
                        >
                            {t('add_revenue')}
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {revenueFields?.map((field, index) => (
                            <div
                                key={field.id}
                                className="grid grid-cols-12 gap-2 items-start"
                            >
                                <div className="col-span-4">
                                    <FormItem
                                        control={control}
                                        name={`revenues.${index}.item_name`}
                                        type="textfield"
                                        label={
                                            index === 0 ? t('item_name') : ''
                                        }
                                        placeholder={t(
                                            'revenue_name_placeholder'
                                        )}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <FormItem
                                        control={control}
                                        name={`revenues.${index}.amount`}
                                        type="number"
                                        label={index === 0 ? t('amount') : ''}
                                        placeholder="0"
                                    />
                                </div>
                                <div className="col-span-4">
                                    <FormItem
                                        control={control}
                                        name={`revenues.${index}.notes`}
                                        type="textfield"
                                        label={index === 0 ? t('notes') : ''}
                                        placeholder={t('notes_placeholder')}
                                    />
                                </div>
                                <div className="col-span-1 flex items-center justify-center">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        className="border hover:border-red-500 hover:text-red-500"
                                        onClick={() => removeRevenue(index)}
                                    >
                                        <DeleteIcons />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Expenses Section */}
                <div className="space-y-2 bg-gray-50 p-4 rounded-md">
                    <div className="flex justify-between items-center">
                        <Typogaphy className="font-medium text-primary">
                            {t('expenses')}
                        </Typogaphy>
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<AddIcons />}
                            onClick={() =>
                                appendExpense({
                                    item_name: '',
                                    amount: 0,
                                    notes: '',
                                })
                            }
                        >
                            {t('add_expense')}
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {expenseFields.map((field, index) => (
                            <div
                                key={field.id}
                                className="grid grid-cols-12 gap-2 items-start"
                            >
                                <div className="col-span-4">
                                    <FormItem
                                        control={control}
                                        name={`expenses.${index}.item_name`}
                                        type="textfield"
                                        label={
                                            index === 0 ? t('item_name') : ''
                                        }
                                        placeholder={t(
                                            'expense_name_placeholder'
                                        )}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <FormItem
                                        control={control}
                                        name={`expenses.${index}.amount`}
                                        type="number"
                                        label={index === 0 ? t('amount') : ''}
                                        placeholder="0"
                                    />
                                </div>
                                <div className="col-span-4">
                                    <FormItem
                                        control={control}
                                        name={`expenses.${index}.notes`}
                                        type="textfield"
                                        label={index === 0 ? t('notes') : ''}
                                        placeholder={t('notes_placeholder')}
                                    />
                                </div>
                                <div className="col-span-1 flex items-center justify-center">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        className="border hover:border-red-500 hover:text-red-500"
                                        onClick={() => removeExpense(index)}
                                    >
                                        <DeleteIcons />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                    >
                        {t('create')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreateOtherFinancialModal
